// Tournament UI: bracket visualization, lobby, and tournament management

import {
  TournamentClient,
  TournamentState,
  TournamentPlayer,
  BracketMatch,
  TournamentSize,
} from '../multiplayer/TournamentClient';

export class TournamentUI {
  private container: HTMLElement;
  private client: TournamentClient;
  private currentState: TournamentState | null = null;
  private tournamentScreen: HTMLElement | null = null;
  private onNavigateToMatch: ((gameRoomId: string) => void) | null = null;
  private onBackToMenu: (() => void) | null = null;
  private chatMessages: { from: string; message: string; timestamp: number }[] = [];

  constructor(container: HTMLElement) {
    this.container = container;
    this.client = new TournamentClient();
    this.setupEventHandlers();
  }

  private setupEventHandlers(): void {
    this.client.on('tournamentState', ({ state }) => {
      this.currentState = state;
      this.render();
    });

    this.client.on('playerRegistered', ({ player }) => {
      if (this.currentState) {
        if (!this.currentState.players.find(p => p.playerId === player.playerId)) {
          this.currentState.players.push(player);
        }
        this.render();
      }
    });

    this.client.on('playerLeft', ({ playerId }) => {
      if (this.currentState) {
        this.currentState.players = this.currentState.players.filter(p => p.playerId !== playerId);
        this.render();
      }
    });

    this.client.on('tournamentStarted', ({ state }) => {
      this.currentState = state;
      this.render();
    });

    this.client.on('matchReady', ({ match, gameRoomId }) => {
      if (this.currentState) {
        const idx = this.currentState.bracket.findIndex(m => m.matchId === match.matchId);
        if (idx >= 0) this.currentState.bracket[idx] = match;
        this.render();

        // If this player is in the match, show a join prompt
        const myId = this.client.getPlayerId();
        const me = this.currentState.players.find(p => p.playerId === myId);
        if (me && (match.player1Seed === me.seed || match.player2Seed === me.seed)) {
          this.showMatchJoinPrompt(match, gameRoomId);
        }
      }
    });

    this.client.on('matchResult', ({ match }) => {
      if (this.currentState) {
        const idx = this.currentState.bracket.findIndex(m => m.matchId === match.matchId);
        if (idx >= 0) this.currentState.bracket[idx] = match;
        this.render();
      }
    });

    this.client.on('roundComplete', ({ nextRound, bracket }) => {
      if (this.currentState) {
        this.currentState.bracket = bracket;
        this.currentState.currentRound = nextRound;
        this.render();
      }
    });

    this.client.on('tournamentComplete', ({ winnerId, winnerName, bracket }) => {
      if (this.currentState) {
        this.currentState.phase = 'completed';
        this.currentState.winnerId = winnerId;
        this.currentState.winnerName = winnerName;
        this.currentState.bracket = bracket;
        this.render();
      }
    });

    this.client.on('tournamentChat', (msg) => {
      this.chatMessages.push(msg);
      if (this.chatMessages.length > 50) this.chatMessages.shift();
      this.updateChat();
    });

    this.client.on('countdown', ({ secondsRemaining, matchId }) => {
      this.updateCountdown(matchId, secondsRemaining);
    });

    this.client.on('tournamentError', ({ message }) => {
      this.showError(message);
    });
  }

  setOnNavigateToMatch(cb: (gameRoomId: string) => void): void {
    this.onNavigateToMatch = cb;
  }

  setOnBackToMenu(cb: () => void): void {
    this.onBackToMenu = cb;
  }

  // Show the tournament creation/browse screen
  showTournamentBrowser(): void {
    this.removeTournamentScreen();
    this.tournamentScreen = document.createElement('div');
    this.tournamentScreen.id = 'tournament-screen';
    this.tournamentScreen.innerHTML = `
      <style>
        #tournament-screen {
          position: absolute; inset: 0;
          background: linear-gradient(135deg, #0a0a1a 0%, #1a1a2e 50%, #16213e 100%);
          color: #E8DCC4; font-family: 'Cinzel', serif;
          display: flex; flex-direction: column; align-items: center;
          padding: 40px 20px; overflow-y: auto; z-index: 100;
        }
        .tournament-title {
          font-size: 2.5rem; color: #C9A227; margin-bottom: 10px;
          text-shadow: 0 0 20px rgba(201, 162, 39, 0.5);
        }
        .tournament-subtitle { font-size: 1rem; opacity: 0.7; margin-bottom: 30px; }
        .create-form {
          background: rgba(255,255,255,0.05); border: 1px solid rgba(201,162,39,0.3);
          border-radius: 12px; padding: 30px; max-width: 450px; width: 100%;
        }
        .create-form label { display: block; margin-bottom: 6px; font-size: 0.9rem; color: #C9A227; }
        .create-form input, .create-form select {
          width: 100%; padding: 10px 14px; margin-bottom: 18px;
          background: rgba(0,0,0,0.4); border: 1px solid rgba(201,162,39,0.3);
          border-radius: 8px; color: #E8DCC4; font-family: 'Cinzel', serif; font-size: 1rem;
          box-sizing: border-box;
        }
        .create-form input:focus, .create-form select:focus {
          outline: none; border-color: #C9A227;
        }
        .btn-tournament {
          display: inline-block; padding: 12px 28px; border: 2px solid #C9A227;
          background: rgba(201,162,39,0.15); color: #C9A227; cursor: pointer;
          font-family: 'Cinzel', serif; font-size: 1rem; border-radius: 8px;
          transition: all 0.2s; margin: 4px;
        }
        .btn-tournament:hover { background: rgba(201,162,39,0.3); transform: translateY(-1px); }
        .btn-tournament.primary { background: rgba(201,162,39,0.25); font-weight: bold; }
        .btn-back {
          position: absolute; top: 20px; left: 20px; background: none; border: 1px solid rgba(201,162,39,0.3);
          color: #C9A227; padding: 8px 16px; cursor: pointer; border-radius: 6px;
          font-family: 'Cinzel', serif; font-size: 0.85rem;
        }
        .btn-back:hover { background: rgba(201,162,39,0.15); }
        .join-section { margin-top: 20px; max-width: 450px; width: 100%; }
        .join-section input {
          width: 100%; padding: 10px 14px;
          background: rgba(0,0,0,0.4); border: 1px solid rgba(201,162,39,0.3);
          border-radius: 8px; color: #E8DCC4; font-family: 'Cinzel', serif; font-size: 1rem;
          box-sizing: border-box; margin-bottom: 10px;
        }
        .error-msg { color: #ff6b6b; font-size: 0.85rem; margin-top: 8px; min-height: 20px; }
      </style>
      <button class="btn-back" id="btn-tournament-back">Back to Menu</button>
      <div class="tournament-title">Tournaments</div>
      <div class="tournament-subtitle">Create or join a bracket tournament</div>
      <div class="create-form">
        <label for="tournament-name">Tournament Name</label>
        <input type="text" id="tournament-name" placeholder="Grand Wizard Tournament" maxlength="50" />
        <label for="tournament-size">Bracket Size</label>
        <select id="tournament-size">
          <option value="8">8 Players</option>
          <option value="16">16 Players</option>
          <option value="32">32 Players</option>
        </select>
        <div style="text-align:center;">
          <button class="btn-tournament primary" id="btn-create-tournament">Create Tournament</button>
        </div>
        <div class="error-msg" id="create-error"></div>
      </div>
      <div class="join-section">
        <div style="text-align:center; margin: 20px 0 10px; font-size: 0.9rem; opacity: 0.6;">- or join an existing tournament -</div>
        <input type="text" id="tournament-join-id" placeholder="Paste tournament ID or link" />
        <div style="text-align:center;">
          <button class="btn-tournament" id="btn-join-tournament">Join Tournament</button>
        </div>
        <div class="error-msg" id="join-error"></div>
      </div>
    `;
    this.container.appendChild(this.tournamentScreen);

    // Back button
    document.getElementById('btn-tournament-back')?.addEventListener('click', () => {
      this.removeTournamentScreen();
      this.onBackToMenu?.();
    });

    // Create tournament
    document.getElementById('btn-create-tournament')?.addEventListener('click', async () => {
      const nameInput = document.getElementById('tournament-name') as HTMLInputElement;
      const sizeSelect = document.getElementById('tournament-size') as HTMLSelectElement;
      const errorDiv = document.getElementById('create-error')!;

      const name = nameInput.value.trim() || 'Wizard Tournament';
      const size = parseInt(sizeSelect.value) as TournamentSize;

      try {
        errorDiv.textContent = '';
        const tournamentId = await this.client.createTournament(name, size);
        this.joinTournament(tournamentId);
      } catch (e) {
        errorDiv.textContent = (e as Error).message;
      }
    });

    // Join tournament
    document.getElementById('btn-join-tournament')?.addEventListener('click', () => {
      const idInput = document.getElementById('tournament-join-id') as HTMLInputElement;
      const errorDiv = document.getElementById('join-error')!;

      let tournamentId = idInput.value.trim();
      // Extract ID from URL if pasted
      const urlMatch = tournamentId.match(/tournament\/([a-z0-9_]+)/);
      if (urlMatch) tournamentId = urlMatch[1];

      if (!tournamentId) {
        errorDiv.textContent = 'Please enter a tournament ID';
        return;
      }

      errorDiv.textContent = '';
      this.joinTournament(tournamentId);
    });
  }

  joinTournament(tournamentId: string): void {
    this.client.connect(tournamentId);
    // The render will be triggered by the tournamentState event
  }

  // Render the full tournament lobby/bracket view
  private render(): void {
    if (!this.currentState) return;

    this.removeTournamentScreen();
    this.tournamentScreen = document.createElement('div');
    this.tournamentScreen.id = 'tournament-screen';

    const state = this.currentState;
    const isCreator = state.creatorId === this.client.getPlayerId();

    this.tournamentScreen.innerHTML = `
      <style>
        #tournament-screen {
          position: absolute; inset: 0;
          background: linear-gradient(135deg, #0a0a1a 0%, #1a1a2e 50%, #16213e 100%);
          color: #E8DCC4; font-family: 'Cinzel', serif;
          display: flex; flex-direction: column; align-items: center;
          padding: 20px; overflow-y: auto; z-index: 100;
        }
        .tournament-header {
          text-align: center; margin-bottom: 15px; width: 100%;
        }
        .tournament-title {
          font-size: 2rem; color: #C9A227; margin: 0;
          text-shadow: 0 0 20px rgba(201, 162, 39, 0.5);
        }
        .tournament-info {
          font-size: 0.85rem; opacity: 0.7; margin-top: 5px;
        }
        .tournament-phase {
          display: inline-block; padding: 4px 14px; border-radius: 20px;
          font-size: 0.8rem; margin-top: 8px; font-weight: bold;
        }
        .phase-registration { background: rgba(52, 152, 219, 0.3); color: #5dade2; border: 1px solid rgba(52, 152, 219, 0.5); }
        .phase-active { background: rgba(46, 204, 113, 0.3); color: #2ecc71; border: 1px solid rgba(46, 204, 113, 0.5); }
        .phase-completed { background: rgba(201, 162, 39, 0.3); color: #C9A227; border: 1px solid rgba(201, 162, 39, 0.5); }

        .tournament-body { display: flex; gap: 20px; width: 100%; max-width: 1200px; flex: 1; min-height: 0; }
        .bracket-panel { flex: 1; overflow-x: auto; overflow-y: auto; }
        .side-panel { width: 280px; display: flex; flex-direction: column; gap: 12px; flex-shrink: 0; }

        /* Bracket styles */
        .bracket-container { display: flex; gap: 0; align-items: center; min-width: fit-content; padding: 10px 0; }
        .bracket-round { display: flex; flex-direction: column; justify-content: center; min-width: 180px; }
        .round-label { text-align: center; color: #C9A227; font-size: 0.75rem; margin-bottom: 10px; font-weight: bold; }
        .bracket-match {
          background: rgba(255,255,255,0.04); border: 1px solid rgba(201,162,39,0.2);
          border-radius: 6px; margin: 4px 0; overflow: hidden; position: relative;
        }
        .bracket-match.active { border-color: rgba(46, 204, 113, 0.6); box-shadow: 0 0 10px rgba(46, 204, 113, 0.2); }
        .bracket-match.completed { opacity: 0.8; }
        .match-player {
          padding: 6px 10px; font-size: 0.8rem; display: flex; justify-content: space-between;
          border-bottom: 1px solid rgba(255,255,255,0.05);
        }
        .match-player:last-child { border-bottom: none; }
        .match-player.winner { color: #2ecc71; font-weight: bold; }
        .match-player.loser { opacity: 0.5; }
        .match-player.tbd { opacity: 0.3; font-style: italic; }
        .match-player .seed { color: #C9A227; font-size: 0.7rem; margin-right: 6px; opacity: 0.7; }
        .match-status {
          position: absolute; top: 50%; right: -24px; transform: translateY(-50%);
          font-size: 0.6rem; color: #C9A227;
        }
        .round-connector {
          width: 24px; display: flex; flex-direction: column; justify-content: center;
        }

        /* Player list */
        .player-list-panel {
          background: rgba(255,255,255,0.04); border: 1px solid rgba(201,162,39,0.2);
          border-radius: 8px; padding: 12px;
        }
        .panel-title { color: #C9A227; font-size: 0.85rem; margin-bottom: 8px; font-weight: bold; }
        .player-item {
          display: flex; justify-content: space-between; align-items: center;
          padding: 5px 0; font-size: 0.8rem; border-bottom: 1px solid rgba(255,255,255,0.05);
        }
        .player-item:last-child { border-bottom: none; }
        .player-item .connected { color: #2ecc71; }
        .player-item .disconnected { color: #e74c3c; }

        /* Chat */
        .chat-panel {
          background: rgba(255,255,255,0.04); border: 1px solid rgba(201,162,39,0.2);
          border-radius: 8px; padding: 12px; display: flex; flex-direction: column; flex: 1; min-height: 150px;
        }
        .chat-messages { flex: 1; overflow-y: auto; font-size: 0.75rem; margin-bottom: 8px; max-height: 200px; }
        .chat-msg { margin-bottom: 3px; }
        .chat-msg .name { color: #C9A227; }
        .chat-input-row { display: flex; gap: 4px; }
        .chat-input-row input {
          flex: 1; padding: 6px 10px; background: rgba(0,0,0,0.4); border: 1px solid rgba(201,162,39,0.3);
          border-radius: 4px; color: #E8DCC4; font-family: 'Cinzel', serif; font-size: 0.75rem;
        }
        .chat-input-row button {
          padding: 6px 12px; background: rgba(201,162,39,0.15); border: 1px solid rgba(201,162,39,0.3);
          color: #C9A227; cursor: pointer; border-radius: 4px; font-family: 'Cinzel', serif; font-size: 0.75rem;
        }

        /* Actions */
        .tournament-actions { text-align: center; margin-top: 10px; }
        .btn-tournament {
          display: inline-block; padding: 10px 24px; border: 2px solid #C9A227;
          background: rgba(201,162,39,0.15); color: #C9A227; cursor: pointer;
          font-family: 'Cinzel', serif; font-size: 0.9rem; border-radius: 8px;
          transition: all 0.2s; margin: 4px;
        }
        .btn-tournament:hover { background: rgba(201,162,39,0.3); transform: translateY(-1px); }
        .btn-tournament.primary { background: rgba(201,162,39,0.25); font-weight: bold; }
        .btn-tournament.danger { border-color: #e74c3c; color: #e74c3c; background: rgba(231,76,60,0.1); }
        .btn-back {
          position: absolute; top: 15px; left: 15px; background: none; border: 1px solid rgba(201,162,39,0.3);
          color: #C9A227; padding: 6px 14px; cursor: pointer; border-radius: 6px;
          font-family: 'Cinzel', serif; font-size: 0.8rem; z-index: 10;
        }
        .btn-back:hover { background: rgba(201,162,39,0.15); }

        /* Share link */
        .share-section {
          background: rgba(255,255,255,0.04); border: 1px solid rgba(201,162,39,0.2);
          border-radius: 8px; padding: 12px; text-align: center;
        }
        .share-link {
          font-size: 0.7rem; color: #5dade2; word-break: break-all;
          padding: 6px; background: rgba(0,0,0,0.3); border-radius: 4px; margin: 6px 0;
          cursor: pointer;
        }
        .share-link:hover { color: #85c1e9; }
        .copy-feedback { font-size: 0.7rem; color: #2ecc71; min-height: 16px; }

        /* Winner banner */
        .winner-banner {
          background: linear-gradient(135deg, rgba(201,162,39,0.2), rgba(201,162,39,0.05));
          border: 2px solid #C9A227; border-radius: 12px; padding: 20px; text-align: center;
          margin-bottom: 15px;
        }
        .winner-banner .crown { font-size: 2.5rem; margin-bottom: 5px; }
        .winner-banner .winner-name { font-size: 1.5rem; color: #C9A227; font-weight: bold; }
        .winner-banner .winner-label { font-size: 0.85rem; opacity: 0.7; }

        /* Match join prompt */
        .match-prompt {
          position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%);
          background: rgba(10, 10, 26, 0.95); border: 2px solid #C9A227;
          border-radius: 12px; padding: 30px; text-align: center; z-index: 200;
          box-shadow: 0 0 40px rgba(201, 162, 39, 0.3);
        }
        .match-prompt h3 { color: #C9A227; margin: 0 0 10px; font-size: 1.3rem; }
        .match-prompt p { margin: 0 0 20px; font-size: 0.95rem; }
        .match-prompt .countdown-text { font-size: 1.5rem; color: #e74c3c; margin: 10px 0; }
      </style>
      <button class="btn-back" id="btn-tournament-back">Back</button>
      <div class="tournament-header">
        <div class="tournament-title">${this.escapeHtml(state.name)}</div>
        <div class="tournament-info">
          ${state.size}-Player Bracket | Round ${state.currentRound} of ${state.totalRounds}
        </div>
        <span class="tournament-phase phase-${state.phase}">
          ${state.phase === 'registration' ? `Registration (${state.players.length}/${state.size})` :
            state.phase === 'active' ? `Round ${state.currentRound}` : 'Completed'}
        </span>
      </div>

      ${state.phase === 'completed' && state.winnerName ? `
        <div class="winner-banner">
          <div class="crown">&#9813;</div>
          <div class="winner-label">Tournament Champion</div>
          <div class="winner-name">${this.escapeHtml(state.winnerName)}</div>
        </div>
      ` : ''}

      <div class="tournament-body">
        <div class="bracket-panel">
          ${state.phase !== 'registration' ? this.renderBracket(state) : this.renderRegistrationWaiting(state)}
        </div>
        <div class="side-panel">
          ${this.renderShareSection(state)}
          ${this.renderPlayerList(state)}
          ${state.phase === 'registration' && isCreator && state.players.length >= 2 ? `
            <div class="tournament-actions">
              <button class="btn-tournament primary" id="btn-force-start">Start Tournament</button>
            </div>
          ` : ''}
          ${this.renderChatPanel()}
        </div>
      </div>
    `;
    this.container.appendChild(this.tournamentScreen);

    // Wire up event listeners
    document.getElementById('btn-tournament-back')?.addEventListener('click', () => {
      this.client.disconnect();
      this.removeTournamentScreen();
      this.onBackToMenu?.();
    });

    document.getElementById('btn-force-start')?.addEventListener('click', () => {
      this.client.startTournament();
    });

    document.getElementById('btn-copy-link')?.addEventListener('click', () => {
      const link = `${window.location.origin}/tournament/${state.tournamentId}`;
      navigator.clipboard.writeText(link).then(() => {
        const feedback = document.getElementById('copy-feedback');
        if (feedback) {
          feedback.textContent = 'Copied!';
          setTimeout(() => { if (feedback) feedback.textContent = ''; }, 2000);
        }
      });
    });

    // Chat
    const chatInput = document.getElementById('tournament-chat-input') as HTMLInputElement;
    const chatSend = document.getElementById('btn-chat-send');
    const sendChat = () => {
      if (chatInput?.value.trim()) {
        this.client.sendChat(chatInput.value.trim());
        chatInput.value = '';
      }
    };
    chatSend?.addEventListener('click', sendChat);
    chatInput?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') sendChat();
    });

    // Wire up spectate buttons
    this.tournamentScreen.querySelectorAll('.btn-spectate').forEach(btn => {
      btn.addEventListener('click', () => {
        const roomId = (btn as HTMLElement).dataset.roomId;
        if (roomId && this.onNavigateToMatch) {
          this.onNavigateToMatch(roomId);
        }
      });
    });
  }

  private renderBracket(state: TournamentState): string {
    const rounds: BracketMatch[][] = [];
    for (let r = 1; r <= state.totalRounds; r++) {
      rounds.push(state.bracket.filter(m => m.round === r));
    }

    const roundNames = this.getRoundNames(state.totalRounds);

    let html = '<div class="bracket-container">';
    rounds.forEach((roundMatches, ri) => {
      html += '<div class="bracket-round">';
      html += `<div class="round-label">${roundNames[ri]}</div>`;
      roundMatches.forEach(match => {
        html += this.renderMatch(match);
      });
      html += '</div>';
      // Add connectors between rounds (except after last)
      if (ri < rounds.length - 1) {
        html += '<div class="round-connector"></div>';
      }
    });
    html += '</div>';
    return html;
  }

  private renderMatch(match: BracketMatch): string {
    const isActive = match.status === 'in_progress';
    const isComplete = match.status === 'completed' || match.status === 'forfeit';
    const p1Won = isComplete && match.winnerSeed === match.player1Seed;
    const p2Won = isComplete && match.winnerSeed === match.player2Seed;

    const p1Class = isComplete ? (p1Won ? 'winner' : 'loser') : (match.player1Name ? '' : 'tbd');
    const p2Class = isComplete ? (p2Won ? 'winner' : 'loser') : (match.player2Name ? '' : 'tbd');

    const matchClass = isActive ? 'active' : (isComplete ? 'completed' : '');

    let spectateBtn = '';
    if (isActive && match.gameRoomId) {
      spectateBtn = `<button class="btn-spectate" data-room-id="${match.gameRoomId}" style="
        position:absolute; top:50%; right:4px; transform:translateY(-50%);
        font-size:0.6rem; padding:2px 6px; background:rgba(46,204,113,0.2);
        border:1px solid rgba(46,204,113,0.4); color:#2ecc71; cursor:pointer;
        border-radius:3px; font-family:'Cinzel',serif;">Watch</button>`;
    }

    const statusLabel = match.status === 'forfeit' ? ' (forfeit)' : '';

    return `
      <div class="bracket-match ${matchClass}" id="match-${match.matchId}">
        <div class="match-player ${p1Class}">
          ${match.player1Seed !== null ? `<span class="seed">${match.player1Seed}</span>` : ''}
          <span>${match.player1Name ? this.escapeHtml(match.player1Name) : 'TBD'}</span>
          ${p1Won ? '<span>&#10003;</span>' : ''}
        </div>
        <div class="match-player ${p2Class}">
          ${match.player2Seed !== null ? `<span class="seed">${match.player2Seed}</span>` : ''}
          <span>${match.player2Name ? this.escapeHtml(match.player2Name) : 'TBD'}</span>
          ${p2Won ? '<span>&#10003;</span>' : ''}
        </div>
        ${spectateBtn}
        ${statusLabel ? `<div style="text-align:center;font-size:0.6rem;opacity:0.5;padding:2px;">${statusLabel}</div>` : ''}
      </div>
    `;
  }

  private renderRegistrationWaiting(state: TournamentState): string {
    const slots: string[] = [];
    for (let i = 0; i < state.size; i++) {
      const player = state.players[i];
      if (player) {
        slots.push(`
          <div class="player-slot filled" style="
            display:flex;align-items:center;gap:8px;padding:8px 12px;
            background:rgba(46,204,113,0.1);border:1px solid rgba(46,204,113,0.3);
            border-radius:6px;margin:4px 0;">
            <span class="seed" style="color:#C9A227;font-size:0.8rem;">#${i + 1}</span>
            <span>${this.escapeHtml(player.name)}</span>
            <span style="margin-left:auto;font-size:0.7rem;opacity:0.6;">${player.elo} ELO</span>
          </div>
        `);
      } else {
        slots.push(`
          <div class="player-slot empty" style="
            display:flex;align-items:center;gap:8px;padding:8px 12px;
            background:rgba(255,255,255,0.02);border:1px dashed rgba(255,255,255,0.1);
            border-radius:6px;margin:4px 0;opacity:0.4;">
            <span class="seed" style="color:#C9A227;font-size:0.8rem;">#${i + 1}</span>
            <span>Waiting for player...</span>
          </div>
        `);
      }
    }

    return `
      <div style="max-width:400px;margin:0 auto;">
        <div style="text-align:center;margin-bottom:15px;font-size:1.1rem;">
          Waiting for players to join
          <div style="font-size:0.8rem;opacity:0.6;margin-top:4px;">
            ${state.players.length} / ${state.size} registered
          </div>
        </div>
        ${slots.join('')}
      </div>
    `;
  }

  private renderShareSection(state: TournamentState): string {
    const link = `${window.location.origin}/tournament/${state.tournamentId}`;
    return `
      <div class="share-section">
        <div class="panel-title">Share Tournament</div>
        <div class="share-link" id="share-link" title="Click to copy">${link}</div>
        <button class="btn-tournament" id="btn-copy-link" style="padding:6px 14px;font-size:0.75rem;">Copy Link</button>
        <div class="copy-feedback" id="copy-feedback"></div>
      </div>
    `;
  }

  private renderPlayerList(state: TournamentState): string {
    const items = state.players.map(p => `
      <div class="player-item">
        <span>${this.escapeHtml(p.name)} ${p.playerId === state.creatorId ? '(host)' : ''}</span>
        <span class="${p.connected ? 'connected' : 'disconnected'}">
          ${p.connected ? 'Online' : 'Offline'}
        </span>
      </div>
    `).join('');

    return `
      <div class="player-list-panel">
        <div class="panel-title">Players (${state.players.length}/${state.size})</div>
        ${items}
      </div>
    `;
  }

  private renderChatPanel(): string {
    const msgs = this.chatMessages.map(m => `
      <div class="chat-msg">
        <span class="name">${this.escapeHtml(m.from)}:</span> ${this.escapeHtml(m.message)}
      </div>
    `).join('');

    return `
      <div class="chat-panel">
        <div class="panel-title">Chat</div>
        <div class="chat-messages" id="tournament-chat-messages">${msgs}</div>
        <div class="chat-input-row">
          <input type="text" id="tournament-chat-input" placeholder="Type a message..." maxlength="200" />
          <button id="btn-chat-send">Send</button>
        </div>
      </div>
    `;
  }

  private showMatchJoinPrompt(match: BracketMatch, gameRoomId: string): void {
    // Remove any existing prompt
    document.getElementById('match-join-prompt')?.remove();

    const opponentSeed = match.player1Seed === this.getMyPlayer()?.seed ? match.player2Seed : match.player1Seed;
    const opponentName = match.player1Seed === this.getMyPlayer()?.seed ? match.player2Name : match.player1Name;

    const prompt = document.createElement('div');
    prompt.id = 'match-join-prompt';
    prompt.className = 'match-prompt';
    prompt.innerHTML = `
      <h3>Your Match is Ready!</h3>
      <p>You are facing <strong>${this.escapeHtml(opponentName || 'TBD')}</strong> (Seed #${opponentSeed})</p>
      <div class="countdown-text" id="match-countdown">60</div>
      <p style="font-size:0.8rem;opacity:0.6;">Join within 60 seconds or forfeit</p>
      <button class="btn-tournament primary" id="btn-join-match">Enter Match</button>
    `;
    document.body.appendChild(prompt);

    document.getElementById('btn-join-match')?.addEventListener('click', () => {
      prompt.remove();
      this.onNavigateToMatch?.(gameRoomId);
    });
  }

  private updateCountdown(_matchId: string, seconds: number): void {
    const el = document.getElementById('match-countdown');
    if (el) {
      el.textContent = String(seconds);
      if (seconds <= 5) el.style.color = '#e74c3c';
    }
  }

  private updateChat(): void {
    const container = document.getElementById('tournament-chat-messages');
    if (!container) return;

    const lastMsg = this.chatMessages[this.chatMessages.length - 1];
    if (lastMsg) {
      const div = document.createElement('div');
      div.className = 'chat-msg';
      div.innerHTML = `<span class="name">${this.escapeHtml(lastMsg.from)}:</span> ${this.escapeHtml(lastMsg.message)}`;
      container.appendChild(div);
      container.scrollTop = container.scrollHeight;
    }
  }

  private showError(message: string): void {
    const errorDiv = document.getElementById('create-error') || document.getElementById('join-error');
    if (errorDiv) errorDiv.textContent = message;
  }

  private getMyPlayer(): TournamentPlayer | undefined {
    return this.currentState?.players.find(p => p.playerId === this.client.getPlayerId());
  }

  private getRoundNames(totalRounds: number): string[] {
    const names: string[] = [];
    for (let r = 1; r <= totalRounds; r++) {
      if (r === totalRounds) names.push('Final');
      else if (r === totalRounds - 1) names.push('Semi-Final');
      else if (r === totalRounds - 2) names.push('Quarter-Final');
      else names.push(`Round ${r}`);
    }
    return names;
  }

  private escapeHtml(str: string): string {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  private removeTournamentScreen(): void {
    if (this.tournamentScreen) {
      this.tournamentScreen.remove();
      this.tournamentScreen = null;
    }
    // Also remove by ID in case of stale references
    document.getElementById('tournament-screen')?.remove();
  }

  destroy(): void {
    this.client.disconnect();
    this.removeTournamentScreen();
    document.getElementById('match-join-prompt')?.remove();
  }
}
